/**
 * What a receipt can honestly say, derived from records that exist (YAG-372).
 *
 * The receipt is written for the person who was not in the room, which makes
 * every fabricated line on it worse than a missing one. So this module answers
 * one question per row: is there a record for this? A row with no record does
 * not render.
 *
 * Deliberately absent: the Number. Number data is YAG-374's, and a receipt that
 * claimed a metric moved because of this change would be exactly the automatic
 * causal tracing the scoping addendum rules out.
 */
import type {
  VerifyResult,
  WorkItem,
  WorkItemActivityDto,
  WorkItemGate,
  WorkItemReviewState,
} from "@yagni/shared";
import { RECEIPT_ROLES, stampByLine, type ReceiptRole } from "./workCopy.js";
import { AGENT_REVIEW_STATE_COPY } from "./workCopy.js";

/** `Aug 4`, the one date shape a receipt prints. */
function dayStamp(iso: string | null): string | null {
  if (!iso) return null;
  const when = new Date(iso);
  return Number.isNaN(when.getTime())
    ? null
    : when.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/**
 * YAGNI merged the pull request on its own, on the Team's "Merge
 * automatically" switch: the line the automatic merge writes on the ticket
 * (`codeReview/autoMerge.ts`). A person's click, in YAGNI or on GitHub, is not.
 */
export function mergedByYagni(activity: readonly WorkItemActivityDto[]): boolean {
  return activity.some((entry) => entry.metadata?.autoMerged === true);
}

/** The settled row: how the item ended, from its phase and its record. */
function settledLine(item: WorkItem, activity: readonly WorkItemActivityDto[]): string {
  if (item.phase !== "done") {
    return item.discard ? "discarded and started over" : "closed with no work done";
  }
  return mergedByYagni(activity) ? "merged by YAGNI and on the record" : "done and on the record";
}

export interface ReceiptCredit {
  role: ReceiptRole;
  /** The one line to the right of the role word. */
  line: string;
  /** The worker whose act it was, when one is on the record. */
  personaSlug: string | null;
}

/**
 * Why the Team withdrew its own proposal, as its resolved accept gate records
 * it (`proposal_withdrawn_by_team` with a reason: the ticket closed, shipped,
 * or left the Team's backlog). Null when no gate says so.
 */
export function teamWithdrawalReason(gates: readonly WorkItemGate[]): string | null {
  for (let at = gates.length - 1; at >= 0; at--) {
    const gate = gates[at]!;
    if (gate.kind !== "accept_proposal" || gate.status !== "resolved") continue;
    if (gate.resolution?.proposal_withdrawn_by_team !== true) continue;
    const reason = gate.resolution.reason;
    if (typeof reason === "string" && reason.trim()) return reason.trim();
  }
  return null;
}

/** The verification line, and only from a real verify record. */
export function receiptVerification(gates: WorkItemGate[]): string | null {
  // A `verify_receipt` gate is MINTED, never resolved through the resolve
  // route: it is a read-only record of a deterministic source check, and the
  // Feed's own mark-handled control is what clears it. So the record we key on
  // is the stamped `verify_result` in its payload, not a resolution that by
  // construction never arrives. A gate with no readable result says nothing.
  for (const gate of gates) {
    if (gate.kind !== "verify_receipt") continue;
    const raw = gate.payload?.verify_result;
    if (!raw || typeof raw !== "object") continue;
    const result = raw as Partial<VerifyResult>;
    if (result.status !== "pass" || typeof result.detail !== "string") continue;
    const when = gate.openedAt ? new Date(gate.openedAt) : null;
    const stamp =
      when && !Number.isNaN(when.getTime())
        ? when.toLocaleDateString("en-US", { month: "short", day: "numeric" })
        : null;
    return stamp ? `${result.detail} · verified ${stamp}` : result.detail;
  }
  return null;
}

/**
 * The review record's one line, or null when nothing reviewed this.
 *
 * Same rule as everything else here: a clause per fact that exists. The pull
 * request is always named, because the record is only ever about a specific
 * one; the date appears only when a review actually completed, and the stored
 * state is printed verbatim beside the derived word rather than instead of it,
 * because the two can honestly differ (a run can be finished and its findings
 * still be waiting on a person).
 *
 * The status word itself is NOT here: it comes from `PROCTOR_STATUS_CHIP`,
 * which the PR listing already prints, so the receipt and the listing cannot
 * end up calling the same state two different things.
 */
export function receiptReviewLine(state: WorkItemReviewState | null): string | null {
  if (!state) return null;
  const parts = [`pull request #${state.prNumber}`];
  const reviewed = dayStamp(state.lastReviewedAt);
  if (reviewed) parts.push(`last reviewed ${reviewed}`);
  // The stored machine fact, spoken: the raw enum stays internal vocabulary,
  // the receipt says what it means in the same words the chips use.
  parts.push(`recorded as ${AGENT_REVIEW_STATE_COPY[state.agentReviewState]}`);
  return parts.join(" · ");
}

/**
 * Who an automatic plan approval built as when no person started the plan
 * (the work queue's), from the resolved plan gate's `ranAs`: the plan line's
 * supervisor, else the Team's owner. Null for every other approval.
 */
export function planRanAs(gate: WorkItemGate): string | null {
  if (gate.resolution?.auto !== true) return null;
  const ranAs = gate.resolution.ranAs;
  if (!ranAs || typeof ranAs !== "object" || Array.isArray(ranAs)) return null;
  const { name, as } = ranAs as { name?: unknown; as?: unknown };
  const role =
    as === "line_supervisor" ? "line supervisor" : as === "team_owner" ? "Team owner" : null;
  if (!role) return null;
  const who = typeof name === "string" ? name.trim() : "";
  return who ? `ran as ${who} (${role})` : `ran as the ${role}`;
}

/** The planned row for an approved plan: who approved it, or who an automatic approval built as. */
function approvedPlanLine(approve: WorkItemGate): string {
  const ranAs = planRanAs(approve);
  if (ranAs) return `a plan, approved automatically · ${ranAs}`;
  return `a plan, approved ${stampByLine(approve.resolvedByName ?? null, null)}`;
}

/**
 * The "who did what" grid. One row per role that has a record; roles with
 * nothing behind them are left out entirely rather than rendered blank.
 *
 * The review record deliberately does NOT widen the `reviewed` row. That row
 * answers who decided the findings, which only a resolved gate records; what
 * checked the work is a different question and gets its own section, so no fact
 * is printed twice and neither place implies the other happened.
 */
export function receiptCredits(input: {
  item: WorkItem;
  activity: WorkItemActivityDto[];
  gates: WorkItemGate[];
}): ReceiptCredit[] {
  const { item, activity, gates } = input;
  const worker = item.personaSlug ?? null;
  const resolved = (kind: string) =>
    gates.find((gate) => gate.kind === kind && gate.status === "resolved") ?? null;
  const happened = (type: string) => activity.some((entry) => entry.type === type);

  const credits: Partial<Record<ReceiptRole, ReceiptCredit>> = {};

  const accept = resolved("accept_proposal");
  if (accept) {
    credits.proposed = {
      role: "proposed",
      line: `accepted ${stampByLine(accept.resolvedByName ?? null, null)}`,
      personaSlug: worker,
    };
  }

  const approve = resolved("approve_execution_plan");
  if (approve || happened("plan_created") || happened("plan_recorded")) {
    credits.planned = {
      role: "planned",
      line: approve ? approvedPlanLine(approve) : "a plan was drafted",
      personaSlug: worker,
    };
  }

  if (item.prUrl || happened("execution_started")) {
    credits.built = {
      role: "built",
      line:
        typeof item.prNumber === "number"
          ? `pull request #${item.prNumber}`
          : item.prUrl
            ? "a pull request"
            : "the work ran",
      personaSlug: worker,
    };
  }

  const review = resolved("review_result");
  if (review) {
    const decisions = Array.isArray(review.resolution?.findings)
      ? (review.resolution.findings as unknown[]).length
      : 0;
    credits.reviewed = {
      role: "reviewed",
      line: decisions
        ? `reviewed · ${decisions} finding${decisions === 1 ? "" : "s"} decided ${stampByLine(review.resolvedByName ?? null, null)}`
        : `reviewed ${stampByLine(review.resolvedByName ?? null, null)}`,
      personaSlug: worker,
    };
  }

  if (item.phase === "done" || item.phase === "dismissed") {
    credits.settled = {
      role: "settled",
      line: settledLine(item, activity),
      // A settle is not a worker's act; it is the item's own end state.
      personaSlug: null,
    };
  }

  return RECEIPT_ROLES.map((role) => credits[role]).filter(
    (credit): credit is ReceiptCredit => credit != null,
  );
}
